// Pick an image for a place field — from the UofMPics library or by uploading
// from your computer. Stores a RELATIVE library path (see utils/placeImages.js).
// `mode="single"` returns one path via onChange(path); `mode="multi"` manages a
// list via value:[] + onChange(newList).
import { useEffect, useMemo, useRef, useState } from 'react';
import { picUrl, listImages, uploadImage } from './placeImages.js';

export default function ImagePicker({ label, mode = 'single', value, onChange, uploadFolder = '' }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (open && all === null) listImages().then(setAll); }, [open, all]);

  const results = useMemo(() => {
    const list = all || [];
    const t = q.trim().toLowerCase();
    const base = uploadFolder && !t ? list.filter(p => p.startsWith(uploadFolder)) : list;
    return (t ? list.filter(p => p.toLowerCase().includes(t)) : base).slice(0, 200);
  }, [all, q, uploadFolder]);

  const single = mode === 'single';
  const items = single ? (value ? [value] : []) : (value || []);

  const choose = (path) => {
    if (single) { onChange(path); setOpen(false); }
    else if (!items.includes(path)) onChange([...(value || []), path]);
  };
  const remove = (path) => {
    if (single) onChange('');
    else onChange((value || []).filter(p => p !== path));
  };
  const onUpload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadImage(f, uploadFolder)); // eslint-disable-line no-await-in-loop
      setAll(a => [...new Set([...(a || []), ...paths])]);
      if (single) { onChange(paths[0]); setOpen(false); }
      else onChange([...(value || []), ...paths]);
    } catch (err) {
      alert('Upload failed: ' + (err?.message || err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="imgpick">
      {label && <span className="pf-label">{label}</span>}
      <div className="imgpick-thumbs">
        {items.map(p => (
          <div className="imgpick-thumb" key={p} title={p}>
            <img src={picUrl(p)} alt="" />
            <button className="imgpick-x" onClick={() => remove(p)} aria-label="Remove">✕</button>
          </div>
        ))}
        <button className="imgpick-add" onClick={() => setOpen(o => !o)}>
          {single && items.length ? 'Change' : '＋ Add image'}
        </button>
      </div>

      {open && (
        <div className="imgpick-panel">
          <div className="imgpick-panel-head">
            <input className="pf-input" placeholder="Search library…" value={q} onChange={e => setQ(e.target.value)} />
            <button className="pf-btn" onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? 'Uploading…' : '⬆ Upload'}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple={!single}
              style={{ display: 'none' }} onChange={onUpload} />
          </div>
          <div className="imgpick-grid">
            {all === null && <div className="imgpick-empty">Loading…</div>}
            {all !== null && results.length === 0 && <div className="imgpick-empty">No images found.</div>}
            {results.map(p => (
              <button key={p} className={`imgpick-cell ${items.includes(p) ? 'sel' : ''}`} title={p} onClick={() => choose(p)}>
                <img src={picUrl(p)} alt="" loading="lazy" />
                <span className="imgpick-cell-name">{p.split('/').pop()}</span>
              </button>
            ))}
          </div>
          <button className="pf-btn imgpick-close" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}
